import * as React from 'react';

import { Badge, Button, Label } from '@/components/cargofl-ui';
import { useTeams } from '@/lib/people-api';

/**
 * Which teams may see this customer, project or activity.
 *
 * Empty means everybody, which is the source system's rule and the one the
 * booking pickers apply. Said in words on the control, because "no teams
 * selected" reads like a restriction rather than the absence of one - and
 * getting that backwards hides a project from the whole company.
 */
export function TeamPicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const { data: teams } = useTeams();

  return (
    <div className="space-y-1.5">
      <Label>Visible to</Label>
      <div className="flex flex-wrap gap-1.5">
        {(teams ?? []).map((team) => {
          const on = value.includes(team.id);
          return (
            <Button hint={"Include or leave out this team"}
              key={team.id}
              type="button"
              variant={on ? 'default' : 'outline'}
              size="sm"
              className="h-auto py-1 text-xs font-normal"
              onClick={() =>
                onChange(on ? value.filter((id) => id !== team.id) : [...value, team.id])
              }
            >
              {team.name}
            </Button>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">
        {value.length === 0 ? (
          <>
            <Badge variant="secondary" className="mr-1.5 font-normal">everyone</Badge>
            Nobody is restricted. Choose teams to limit who can book to it.
          </>
        ) : (
          `Only members of ${value.length === 1 ? 'this team' : 'these teams'} can book to it. Administrators always see it.`
        )}
      </p>
    </div>
  );
}
